/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useLocale } from '@/hooks/useLocale';
import styles from './Price.module.scss';
import { formatPrice } from '@/utils/validator/formatPrice';
import { getCurrencySymbol } from '@/utils/priceHelpers';
import { useLabels } from '@/hooks/useLabels';
import { Free } from '@/components/AZCustomComponent/Price/Free';
import StrikethroughPrice from './StrikethroughPrice';

const labelMap = {
  lblEach: 'label_deals_Each',
};

type Props = {
  dealPrice?: string[] | null;
  wasDealPrice?: string[] | null;
  each?: boolean;
  renderFreeInsteadOfZero?: boolean;
  showPlus?: boolean;
  tabIndex?: number;
};

const Price = ({
  dealPrice,
  wasDealPrice,
  each = false,
  renderFreeInsteadOfZero = false,
  showPlus = false,
  tabIndex,
}: Props) => {
  const locale = useLocale();
  const currencySymbol = getCurrencySymbol(locale);
  const labels = useLabels(labelMap);

  if (!dealPrice) {
    return null;
  }

  const dealPriceIsZero = Number(dealPrice[0]) === 0 && Number(dealPrice[1]) === 0;
  const displayFreeWhenDealPriceIsZero = dealPriceIsZero && renderFreeInsteadOfZero;
  const fullDealPrice = dealPrice.join('.').split('.');

  const dealPricingDollars =
    dealPrice[0].includes(',') || dealPrice[0] === '0'
      ? dealPrice[0]
      : formatPrice(Number(dealPrice[0]));
  const formattedPricingDollars =
    dealPricingDollars !== null && dealPricingDollars !== 0
      ? dealPricingDollars.split('.')
      : dealPrice[0];

  if (displayFreeWhenDealPriceIsZero) {
    return (
      <div className={styles.container}>
        <Free />
        <StrikethroughPrice dealPrice={dealPrice} wasDealPrice={wasDealPrice} tabIndex={tabIndex} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.priceRow}>
        <div className={styles.mainPrice}>
          <span className="sr-only">
            Total price is: {fullDealPrice[0]} dollars and {fullDealPrice[1]} cents.
          </span>
          {showPlus && (
            <span className={styles.plusSymbol} aria-hidden="true">
              +
            </span>
          )}
          <span className={styles.currencySymbol} aria-hidden="true">
            {currencySymbol}
          </span>
          <span className={styles.dollars} aria-hidden="true">
            {formattedPricingDollars[0]}
          </span>
          <span className={styles.cents} aria-hidden="true">
            {dealPrice[1]}
          </span>
        </div>
        {each && <div className={styles.eachLabel}>{labels.lblEach}</div>}
      </div>
      <StrikethroughPrice dealPrice={dealPrice} wasDealPrice={wasDealPrice} tabIndex={tabIndex} />
    </div>
  );
};

export default Price;
